import React from "react";

/**
 * Minimal markdown renderer for the gateway AI Assistant.
 *
 * Handles the cases the assistant actually emits:
 *   - paragraphs (blank-line separated)
 *   - bullet lists (`- ` or `* `)
 *   - numbered lists (`1. `)
 *   - inline `code`
 *   - **bold**, *italic*
 *   - inline links [text](url) — rendered as <a target="_blank">
 *   - fenced code blocks ```lang ... ```
 *   - headings (#, ##, ###)
 *   - GFM tables (header, alignment row, body), inside a horizontal scroller
 *
 * Not a full CommonMark implementation — gateway responses are short and
 * structured; we don't need blockquotes, footnotes, task lists, etc.
 */

// Inline format pass: walks the string once, emitting React nodes for code
// spans, bold, italic, and links. Order matters because each rule consumes
// a leading slice of the unmatched remainder.
function renderInline(text, keyPrefix = "i") {
  const out = [];
  let rest = text;
  let idx = 0;
  // Regex with alternation: code | bold | italic | link.
  // Capture groups: 1 code, 2 bold, 3 italic, 4 link text, 5 link url.
  const re = /`([^`\n]+)`|\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*|\[([^\]\n]+)\]\(([^)\s]+)\)/;
  while (rest.length) {
    const m = rest.match(re);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    if (m[1] != null) {
      out.push(<code key={`${keyPrefix}-c-${idx}`}>{m[1]}</code>);
    } else if (m[2] != null) {
      out.push(<strong key={`${keyPrefix}-b-${idx}`}>{m[2]}</strong>);
    } else if (m[3] != null) {
      out.push(<em key={`${keyPrefix}-i-${idx}`}>{m[3]}</em>);
    } else if (m[4] != null && m[5] != null) {
      out.push(
        <a
          key={`${keyPrefix}-l-${idx}`}
          href={m[5]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {m[4]}
        </a>
      );
    }
    rest = rest.slice(m.index + m[0].length);
    idx += 1;
  }
  return out;
}

// Split one GFM table row into cells.
//
// The leading and trailing pipes are optional delimiters, not columns, so they
// are dropped before splitting. `\|` is an escaped pipe *inside* a cell — the
// assistant emits those whenever a value legitimately contains one (a tag
// path, a regex, an OR in a condition), and splitting on them would shift
// every following column of that row one to the left.
function splitRowCells(line) {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|") && !row.endsWith("\\|")) row = row.slice(0, -1);
  const cells = [];
  let current = "";
  for (let i = 0; i < row.length; i += 1) {
    if (row[i] === "\\" && row[i + 1] === "|") {
      current += "|";
      i += 1;
      continue;
    }
    if (row[i] === "|") {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += row[i];
  }
  cells.push(current.trim());
  return cells;
}

const TABLE_DELIMITER_CELL = /^:?-+:?$/;

// Parse a table's alignment row (`|:---|---:|:--:|`), returning one alignment
// per column, or null when the line is not an alignment row at all — which is
// what tells the block loop that a line with pipes in it is just prose.
function parseTableAlignments(line) {
  if (typeof line !== "string" || !line.includes("|")) return null;
  const cells = splitRowCells(line);
  const alignments = [];
  for (const cell of cells) {
    if (!TABLE_DELIMITER_CELL.test(cell)) return null;
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    alignments.push(left && right ? "center" : right ? "right" : left ? "left" : null);
  }
  return alignments;
}

// A table starts where a row of pipes is immediately followed by an alignment
// row. Both are required: one line containing a pipe is a sentence.
function tableAlignmentsAt(lines, index) {
  const line = lines[index];
  if (typeof line !== "string" || !line.includes("|")) return null;
  return parseTableAlignments(lines[index + 1]);
}

function alignStyle(alignment) {
  return alignment ? { textAlign: alignment } : undefined;
}

export default function SimpleMarkdown({ text }) {
  if (typeof text !== "string" || !text) return null;
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block.
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, "").trim();
      const codeLines = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i += 1;
      }
      i += 1; // consume closing ```
      blocks.push({ type: "code", lang, body: codeLines.join("\n") });
      continue;
    }

    // Heading.
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({ type: "heading", level: headingMatch[1].length, body: headingMatch[2] });
      i += 1;
      continue;
    }

    // GFM table. Checked before the lists because the alignment row is the
    // thing that used to leak through as a literal `|---|---|` paragraph.
    const tableAlignments = tableAlignmentsAt(lines, i);
    if (tableAlignments) {
      const header = splitRowCells(line);
      const rows = [];
      i += 2; // header + alignment row
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) {
        rows.push(splitRowCells(lines[i]));
        i += 1;
      }
      blocks.push({ type: "table", header, alignments: tableAlignments, rows });
      continue;
    }

    // Bullet list.
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Blank line.
    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Paragraph — gather consecutive non-blank, non-list, non-code lines.
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^```/.test(lines[i]) &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      // A table that follows prose without a blank line must start a new
      // block instead of being swallowed into the paragraph.
      !tableAlignmentsAt(lines, i)
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: "p", body: paraLines.join(" ") });
  }

  return (
    <>
      {blocks.map((block, bi) => {
        if (block.type === "heading") {
          const Tag = `h${Math.min(6, Math.max(1, block.level))}`;
          return React.createElement(
            Tag,
            { key: `b${bi}`, className: "gwAIMdHeading" },
            renderInline(block.body, `b${bi}h`)
          );
        }
        if (block.type === "code") {
          return (
            <pre key={`b${bi}`} className="gwAIMdCode">
              <code>{block.body}</code>
            </pre>
          );
        }
        if (block.type === "table") {
          // The wrapper, not the table, is what scrolls. The drawer is
          // resizable down to 360px and a six-column table is wider than
          // that; without its own scroller the table either pushes the whole
          // conversation sideways or gets clipped.
          return (
            <div key={`b${bi}`} className="gwAIMdTableWrap">
              <table className="gwAIMdTable">
                <thead>
                  <tr>
                    {block.header.map((cell, ci) => (
                      <th key={ci} style={alignStyle(block.alignments[ci])}>
                        {renderInline(cell, `b${bi}h${ci}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                {block.rows.length > 0 ? (
                  <tbody>
                    {block.rows.map((row, ri) => (
                      <tr key={ri}>
                        {/* Iterate the header, not the row: a short row gets
                            empty trailing cells instead of a ragged table. */}
                        {block.header.map((_, ci) => (
                          <td key={ci} style={alignStyle(block.alignments[ci])}>
                            {renderInline(row[ci] || "", `b${bi}r${ri}c${ci}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ) : null}
              </table>
            </div>
          );
        }
        if (block.type === "ul") {
          return (
            <ul key={`b${bi}`} className="gwAIMdList">
              {block.items.map((it, ii) => (
                <li key={ii}>{renderInline(it, `b${bi}-${ii}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={`b${bi}`} className="gwAIMdList">
              {block.items.map((it, ii) => (
                <li key={ii}>{renderInline(it, `b${bi}-${ii}`)}</li>
              ))}
            </ol>
          );
        }
        // paragraph
        return (
          <p key={`b${bi}`} className="gwAIMdPara">
            {renderInline(block.body, `b${bi}p`)}
          </p>
        );
      })}
    </>
  );
}
